import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, History, Upload, BarChart3, Shield, Languages } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/whatai-logo.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Header = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .single();

      setIsAdmin(!!data);
    };

    checkAdminStatus();
  }, [user]);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <div 
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate("/")}
        >
          <img src={logo} alt="WhatAI Logo" className="w-10 h-10 sm:w-12 sm:h-12" />
          <div className="flex flex-col">
            <span className="font-bold text-xl leading-tight text-primary hidden sm:block">
              WhatAI
            </span>
            <span className="text-xs text-muted-foreground hidden sm:block">
              {t("header.subtitle")}
            </span>
          </div>
        </div>

        <nav className="flex items-center gap-2">
          {/* Language Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-2">
                <Languages className="w-4 h-4" />
                <span className="hidden sm:inline">{language === "he" ? "עברית" : "English"}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-background">
              <DropdownMenuItem onClick={() => setLanguage("he")} className="cursor-pointer">
                🇮🇱 עברית
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage("en")} className="cursor-pointer">
                🇬🇧 English
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {user ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/upload")}
                className="flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">{t("header.newChat")}</span>
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/history")}
                className="flex items-center gap-2"
              >
                <History className="w-4 h-4" />
                <span className="hidden sm:inline">{t("header.myChats")}</span>
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2"
              >
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">{t("header.dashboard")}</span>
              </Button>

              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/admin")}
                  className="flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  <span className="hidden sm:inline">{t("header.admin")}</span>
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={signOut}
                className="flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">{t("header.signOut")}</span>
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              onClick={() => navigate("/auth")}
              className="gradient-primary text-white"
            >
              {t("header.signIn")}
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
